import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
} from "@mui/material";
import { ExpandMoreRounded, ForumRounded } from "@mui/icons-material";
import { format } from "date-fns";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import {
  subscribeMyClinicMessages,
  markClinicMessageRead,
} from "services/clinicService";

const formatTimestamp = (createdAt) => {
  const date = createdAt?.toDate ? createdAt.toDate() : null;
  if (!date) return "";
  return format(date, "MMM d, yyyy 'at' h:mm a");
};

const MessageItem = ({ message, uid }) => {
  const isUnread = message.status !== "read";

  const handleChange = (_, expanded) => {
    if (expanded && isUnread) {
      markClinicMessageRead(uid, message.id).catch((err) =>
        console.error("[MessagesPage] mark read failed:", err),
      );
    }
  };

  const title = message.clinicName || "Your clinic";

  return (
    <Accordion disableGutters onChange={handleChange}>
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ width: "100%", pr: 1 }}
        >
          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: isUnread ? 700 : 500 }}
              noWrap
            >
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTimestamp(message.createdAt)}
            </Typography>
          </Stack>
          {isUnread && <Chip label="New" color="primary" size="small" />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {message.body}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};

const MessagesPage = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid) return undefined;
    setLoading(true);
    const unsubscribe = subscribeMyClinicMessages(
      user.uid,
      (rows) => {
        setMessages(rows);
        setLoading(false);
      },
      (err) => {
        console.error("[MessagesPage] subscription failed:", err);
        setError("Could not load your messages. Please try again.");
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [user?.uid]);

  const unreadCount = messages.filter((m) => m.status !== "read").length;

  return (
    <div className="inner">
      <DashboardPageHeader
        title="Messages"
        subtitle="Messages from clinics you've checked in to appear here."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : messages.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <ForumRounded sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            You have no messages yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            When a clinic you've checked in to sends a message, it will show up here.
          </Typography>
        </Box>
      ) : (
        <>
          {unreadCount > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {unreadCount} unread
            </Typography>
          )}
          <Stack spacing={1}>
            {messages.map((message) => (
              <MessageItem key={message.id} message={message} uid={user.uid} />
            ))}
          </Stack>
        </>
      )}
    </div>
  );
};

export default MessagesPage;
